/**
 * The frame of every docs page: the docs' navigation beside the page, links to the page before
 * and the page after below it, and on the widest screens the page's own sections beside it.
 *
 * On wide screens the navigation is a column that stays in view while the page scrolls. On
 * narrower ones it folds into a button that names the current page and opens the same list, and
 * it closes again when a page is chosen.
 */

import { ArrowLeft, ArrowRight, BookOpen, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";

import { focusRing, styles } from "../../components/ui.js";
import { DOC_GROUPS, DOC_PAGES } from "./nav.js";

export function DocsLayout() {
  return (
    <div
      className={`${styles.page} lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-14 xl:grid-cols-[14rem_minmax(0,1fr)_12rem] xl:gap-12`}
    >
      <DocsNav />
      <div id="docs-page" className="mt-8 min-w-0 lg:mt-0">
        <Outlet />
        <Pager />
      </div>
      <OnThisPage />
    </div>
  );
}

/** A page's link in the navigation: quiet until it is the current page. */
const pageLink = ({ isActive }: { isActive: boolean }) =>
  `block rounded-lg px-3 py-1.5 text-sm transition-colors ${focusRing} ${
    isActive
      ? "bg-brand-500/10 font-semibold text-brand-800 ring-1 ring-brand-600/15 ring-inset dark:bg-white/[0.08] dark:text-white dark:ring-white/10"
      : "text-zinc-600 hover:bg-zinc-900/[0.04] hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/[0.05] dark:hover:text-white"
  }`;

/** The docs' pages by group. Below 1024 px it sits behind a button that names the current page. */
function DocsNav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const current = DOC_PAGES.find((page) => page.to === pathname);

  // biome-ignore lint/correctness/useExhaustiveDependencies: closes the list on every page change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <nav aria-label="Docs" className="lg:sticky lg:top-24 lg:self-start">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="docs-pages"
        onClick={() => setOpen((shown) => !shown)}
        className={`flex w-full items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm ring-1 ring-zinc-900/10 ring-inset lg:hidden dark:bg-white/[0.05] dark:text-zinc-100 dark:shadow-none dark:ring-white/[0.12] ${focusRing}`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <BookOpen aria-hidden className="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
          <span className="text-zinc-500 dark:text-zinc-400">Docs</span>
          <span className="truncate">{current?.label}</span>
        </span>
        <ChevronDown
          aria-hidden
          className={`size-4 shrink-0 text-zinc-500 transition-transform duration-200 dark:text-zinc-400 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id="docs-pages"
        className={`${open ? "mt-2 block" : "hidden"} space-y-6 rounded-xl bg-white p-3 shadow-sm ring-1 ring-zinc-900/10 ring-inset lg:mt-0 lg:block lg:space-y-7 lg:rounded-none lg:bg-transparent lg:p-0 lg:shadow-none lg:ring-0 dark:bg-white/[0.04] dark:shadow-none dark:ring-white/[0.1] lg:dark:bg-transparent`}
      >
        {DOC_GROUPS.map((group) => (
          <div key={group.title}>
            <h2 className="px-3 text-xs font-semibold tracking-[0.12em] text-zinc-500 uppercase dark:text-zinc-400">
              {group.title}
            </h2>
            <ul className="mt-2 space-y-0.5">
              {group.pages.map((page) => (
                <li key={page.to}>
                  <NavLink to={page.to} end className={pageLink}>
                    {page.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

/**
 * The sections of the open page, as links to each. It reads them from the page's headings once the
 * page has drawn, so a page never lists its sections twice. Shown on screens 1280 px wide and
 * more, where there is room beside the text.
 */
function OnThisPage() {
  const { pathname } = useLocation();
  const [sections, setSections] = useState<{ id: string; title: string }[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reads the headings of each new page.
  useEffect(() => {
    const headings = document.querySelectorAll<HTMLHeadingElement>("#docs-page h2[id]");
    setSections(
      Array.from(headings, (heading) => ({ id: heading.id, title: heading.textContent ?? "" })),
    );
  }, [pathname]);

  if (sections.length < 2) {
    return null;
  }
  return (
    <nav aria-label="On this page" className="hidden xl:sticky xl:top-24 xl:block xl:self-start">
      <h2 className="text-xs font-semibold tracking-[0.12em] text-zinc-500 uppercase dark:text-zinc-400">
        On this page
      </h2>
      <ul className="mt-3 space-y-2 border-l border-zinc-900/[0.08] dark:border-white/[0.08]">
        {sections.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className={`-ml-px block border-l border-transparent pl-4 text-sm leading-6 text-zinc-600 transition-colors hover:border-brand-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:border-brand-400 dark:hover:text-white ${focusRing}`}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Links to the page before and the page after the current one, in the docs' reading order. */
function Pager() {
  const { pathname } = useLocation();
  const index = DOC_PAGES.findIndex((page) => page.to === pathname);
  const previous = index > 0 ? DOC_PAGES.at(index - 1) : undefined;
  const next = index >= 0 ? DOC_PAGES.at(index + 1) : undefined;
  if (!previous && !next) {
    return null;
  }
  const card = `group flex flex-1 flex-col gap-1 rounded-xl bg-white px-4 py-3 shadow-[0_1px_2px_rgb(24_24_27/0.04)] ring-1 ring-zinc-900/[0.07] transition hover:ring-brand-600/30 dark:bg-white/[0.03] dark:shadow-none dark:ring-white/[0.08] dark:hover:ring-white/20 ${focusRing}`;
  const hint = "flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400";
  const name = "font-display font-semibold text-zinc-950 dark:text-white";
  return (
    <nav
      aria-label="More docs"
      className="mt-16 flex max-w-3xl flex-col gap-3 border-t border-zinc-900/[0.07] pt-8 sm:flex-row dark:border-white/[0.07]"
    >
      {previous && (
        <Link to={previous.to} className={card}>
          <span className={hint}>
            <ArrowLeft aria-hidden className="size-3.5" />
            Previous
          </span>{" "}
          <span className={name}>{previous.label}</span>
        </Link>
      )}
      {next && (
        <Link to={next.to} className={`${card} sm:items-end sm:text-right`}>
          <span className={hint}>
            Next
            <ArrowRight aria-hidden className="size-3.5" />
          </span>{" "}
          <span className={name}>{next.label}</span>
        </Link>
      )}
    </nav>
  );
}
